import { lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import { AppGuard, sessionModel } from "entities/session";
import { AuthLayout, MainLayout, OnBoardingLayout } from "pages/layouts";
import { PATH_LIST, PATH_PAGE } from "shared/lib/react-router";
import { Loadable } from "shared/ui/loadable";
import { AppLayout } from "pages/layouts/app-layout";
import { VerificationLayout } from "pages/app/verification-steps/verification-layout";
import { VerifyChangedPhonePage } from "pages/app/verify-phone";
import { LoginPage, WelcomeScreen } from "pages/auth";
import {
  ChatPage,
  HomePage,
  MarketPage,
  SupportChatPage,
  WelcomePage,
} from "pages/app";

const Page404 = Loadable(lazy(() => import("pages/page-404")));

// Home pages
const UnfinishedTransactionsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.UnfinishedTransactionsPage,
    })),
  ),
);

// Auth pages
const ForgotPasswordPage = Loadable(
  lazy(() =>
    import("pages/auth").then((module) => ({
      default: module.ForgotPasswordPage,
    })),
  ),
);
const ForgotPasswordVerifyPage = Loadable(
  lazy(() =>
    import("pages/auth").then((module) => ({
      default: module.ForgotPasswordVerifyPage,
    })),
  ),
);

// Onboarding pages
const ChooseAccTypePage = Loadable(
  lazy(() =>
    import("pages/onboarding").then((module) => ({
      default: module.ChooseAccTypePage,
    })),
  ),
);
const PINCodePage = Loadable(
  lazy(() =>
    import("pages/onboarding").then((module) => ({
      default: module.PINCodePage,
    })),
  ),
);
const CreateAccountPage = Loadable(
  lazy(() =>
    import("pages/onboarding").then((module) => ({
      default: module.CreateAccountPage,
    })),
  ),
);
const ResidencyNationalityPage = Loadable(
  lazy(() =>
    import("pages/onboarding").then((module) => ({
      default: module.ResidencyNationalityPage,
    })),
  ),
);

// App pages - Main features
const DiscoverPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.DiscoverPage })),
  ),
);
const ResearchPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.ResearchPage })),
  ),
);
const BlogPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.BlogPage })),
  ),
);
const StrategyPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.StrategyPage })),
  ),
);
const InvestPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.InvestPage })),
  ),
);

// Market pages
const AssetSearchPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.AssetSearchPage })),
  ),
);
const FilterPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.FilterPage })),
  ),
);
const CreateStockFilterPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.CreateStockFilterPage,
    })),
  ),
);
const CreateBondFilterPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.CreateBondFilterPage,
    })),
  ),
);
const FilterReviewPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.FilterReviewPage,
    })),
  ),
);
const StockDetailsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.StockDetailsPage,
    })),
  ),
);
const BondDetailsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.BondDetailsPage })),
  ),
);
const CurrencyDetailsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.CurrencyDetailsPage,
    })),
  ),
);
const OptionDetailsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.OptionDetailsPage,
    })),
  ),
);

// Portfolio pages
const PortfolioPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.PortfolioPage })),
  ),
);
const BuyPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.BuyPage })),
  ),
);
const SellPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.SellPage })),
  ),
);
const HistoryPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.HistoryPage })),
  ),
);
const HistorySearchPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.HistorySearchPage,
    })),
  ),
);

// Account pages
const AccountPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.AccountPage })),
  ),
);
const SubAccountsPage = Loadable(
  lazy(() =>
    import("pages/app/sub-accounts").then((module) => ({
      default: module.SubAccountsPage,
    })),
  ),
);
const PersonalInfoPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.PersonalInfoPage,
    })),
  ),
);
const SecurityPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.SecurityPage })),
  ),
);
const ChangePasswordPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.ChangePasswordPage,
    })),
  ),
);
const ChangePINPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.ChangePINPage })),
  ),
);
const ChangeEmailPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.ChangeEmailPage })),
  ),
);
const ChangePhonePage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.ChangePhonePage })),
  ),
);
const VerifyChangedEmailPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.VerifyChangedEmailPage,
    })),
  ),
);
const TaxInformationPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.TaxInformationPage,
    })),
  ),
);

// Financial pages
const DepositPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.DepositPage })),
  ),
);
const WithdrawalPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.WithdrawalPage })),
  ),
);
const CurrenciesPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.CurrenciesPage })),
  ),
);
const CurrencyOrder = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.CurrencyOrder })),
  ),
);
const RepoDeal = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.RepoDeal })),
  ),
);
const RepoTransaction = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.RepoTransaction })),
  ),
);
const RepoTransactionHistory = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.RepoTransactionHistory,
    })),
  ),
);

// Reports pages
const BrokerageReportPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.BrokerageReportPage,
    })),
  ),
);
const TaxReportPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.TaxReportPage })),
  ),
);

// Assignment pages
const AssignmentPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.AssignmentPage })),
  ),
);
const AssignmentSignContractPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.AssignmentSignContractPage,
    })),
  ),
);
const AssignmentsHistoryPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.AssignmentsHistoryPage,
    })),
  ),
);
const AssignmentsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.AssignmentsPage })),
  ),
);

// Contract pages
const SignUserContractPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.SignUserContractPage,
    })),
  ),
);

// Support pages
const AIChatPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.AIChatPage })),
  ),
);
const QuestionsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.QuestionsPage })),
  ),
);
const TariffsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.TariffsPage })),
  ),
);

// Bonus pages
const MyBonusesPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.MyBonusesPage })),
  ),
);

// Verification pages
const VerificationResidencePage = Loadable(
  lazy(() =>
    import("pages/app/verification-steps/residence").then((module) => ({
      default: module.VerificationResidencePage,
    })),
  ),
);
const BusinessDocumentsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.BusinessDocumentsPage,
    })),
  ),
);
const DetailsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.DetailsPage })),
  ),
);
const DateOfBirthPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.DateOfBirthPage })),
  ),
);
const ResidenceAddressPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.ResidenceAddressPage,
    })),
  ),
);
const PhoneNumberPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.PhoneNumberPage })),
  ),
);
const EmailPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({ default: module.EmailPage })),
  ),
);
const IdentityVerificationPage = Loadable(
  lazy(() =>
    import("pages/app/verification-steps/identity").then((module) => ({
      default: module.IdentityVerificationPage,
    })),
  ),
);
const ChooseDocTypePage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.ChooseDocTypePage,
    })),
  ),
);

const CitizenshipDocumentsPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.CitizenshipDocumentsPage,
    })),
  ),
);

const SelfieWithDocumentPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.SelfieWithDocumentPage,
    })),
  ),
);
const ProofOfAddressPage = Loadable(
  lazy(() =>
    import("pages/app").then((module) => ({
      default: module.ProofOfAddressPage,
    })),
  ),
);

const wrapAppGuard = (Page: any) => (
  <AppGuard>
    <Page />
  </AppGuard>
);

const devMode = process.env.REACT_APP_DEV_MODE === "true";

export function Router() {
  const isAuth = sessionModel.useAuth();

  return useRoutes([
    // layout
    {
      element: <MainLayout />,
      children: [
        //AuthLayout
        {
          path: PATH_PAGE.auth.root,
          element: <AuthLayout />,
          children: [
            {
              path: PATH_PAGE.auth.welcome,
              element: <WelcomeScreen />,
            },
            {
              path: PATH_PAGE.auth.login.root,
              children: [
                {
                  path: "",
                  element: <LoginPage />,
                },
                {
                  path: PATH_PAGE.auth.login.forgotPassword,
                  element: <ForgotPasswordPage />,
                },
                {
                  path: PATH_PAGE.auth.login.forgotPasswordVerify,
                  element: <ForgotPasswordVerifyPage />,
                },
              ],
            },
          ],
        },
        // OnBoardingLayout
        {
          path: PATH_PAGE.onboarding.root,
          element: <OnBoardingLayout />,
          children: [
            {
              path: PATH_PAGE.onboarding.chooseAccType,
              element: <ChooseAccTypePage />,
            },
            {
              path: PATH_PAGE.onboarding.createAccount,
              element: <CreateAccountPage />,
            },

            {
              path: PATH_PAGE.onboarding.residencyNationality,
              element: <ResidencyNationalityPage />,
            },
          ],
        },
      ],
    },
    {
      path: "",
      element: (
        <Navigate to={true ? PATH_LIST.home : PATH_LIST.login} replace />
      ),
    },
    // app
    {
      element: wrapAppGuard(AppLayout),
      path: PATH_PAGE.app.root,
      children: [
        // verification
        {
          path: PATH_PAGE.app.verification.root,
          element: <VerificationLayout />,
          children: [
            {
              index: true,
              element: <WelcomePage />,
            },
            {
              path: PATH_LIST.residence,
              element: <VerificationResidencePage />,
            },
            {
              path: PATH_LIST.businessDocuments,
              element: <BusinessDocumentsPage />,
            },
            {
              path: PATH_LIST.details,
              element: <DetailsPage />,
            },
            {
              path: PATH_LIST.dateOfBirth,
              element: <DateOfBirthPage />,
            },
            {
              path: PATH_LIST.residenceAddress,
              element: <ResidenceAddressPage />,
            },
            {
              path: PATH_LIST.phoneNumber,
              element: <PhoneNumberPage />,
            },
            {
              path: PATH_LIST.email,
              element: <EmailPage />,
            },
            // {
            //   path: PATH_LIST.identityVerification,
            //   element: <IdentityVerificationPage />,
            // },
            {
              path: PATH_LIST.chooseDocType,
              element: <ChooseDocTypePage />,
            },
            {
              path: PATH_LIST.uploadCredentials,
              element: <CitizenshipDocumentsPage />,
            },
            {
              path: PATH_LIST.selfieWithDocument,
              element: <SelfieWithDocumentPage />,
            },
            {
              path: PATH_LIST.proofOfAddress,
              element: <ProofOfAddressPage />,
            },
          ],
        },

        // home
        {
          path: PATH_PAGE.app.home.root,
          children: [
            {
              element: <HomePage />,
              path: "",
            },
            {
              element: <UnfinishedTransactionsPage />,
              path: PATH_PAGE.app.home.unfinishedTransactions,
            },
          ],
        },
        // what to buy
        {
          path: PATH_PAGE.app.discover.root,
          children: [
            {
              element: <DiscoverPage />,
              path: "",
            },
            {
              element: <ResearchPage />,
              path: PATH_PAGE.app.discover.research,
            },
            {
              element: <BlogPage />,
              path: PATH_PAGE.app.discover.blog,
            },
          ],
        },
        // strategy
        {
          element: <StrategyPage />,
          path: PATH_PAGE.app.strategy,
        },
        {
          element: <InvestPage />,
          path: PATH_PAGE.app.invest,
        },
        // market
        {
          path: PATH_PAGE.app.market.root,
          children: [
            {
              element: <MarketPage />,
              path: "",
            },
            {
              element: <AssetSearchPage />,
              path: PATH_PAGE.app.market.search,
            },
            {
              element: <FilterPage />,
              path: PATH_PAGE.app.market.filter.root,
            },
            {
              element: <CreateStockFilterPage />,
              path: PATH_PAGE.app.market.filter.createStockFilter,
            },
            {
              element: <CreateBondFilterPage />,
              path: PATH_PAGE.app.market.filter.createBondFilter,
            },
            {
              element: <FilterReviewPage />,
              path: PATH_PAGE.app.market.filter.filterReview,
            },
            {
              element: <StockDetailsPage />,
              path: PATH_PAGE.app.market.stock,
            },
            {
              element: <BondDetailsPage />,
              path: PATH_PAGE.app.market.bond,
            },
            {
              element: <CurrencyDetailsPage />,
              path: PATH_PAGE.app.market.currency,
            },
            {
              element: <OptionDetailsPage />,
              path: PATH_PAGE.app.market.optionChain,
            },
            {
              element: <BuyPage />,
              path: PATH_PAGE.app.market.buy,
            },
            {
              element: <SellPage />,
              path: PATH_PAGE.app.market.sell,
            },
          ],
        },
        // chat
        {
          path: PATH_PAGE.app.chat.root,
          children: [
            {
              element: <ChatPage />,
              path: "",
            },
            {
              element: <SupportChatPage />,
              path: PATH_PAGE.app.chat.support,
            },
            {
              element: <AIChatPage />,
              path: PATH_PAGE.app.chat.ai,
            },
          ],
        },
        // account
        {
          path: PATH_PAGE.app.account.root,
          children: [
            {
              element: <AccountPage />,
              path: "",
            },

            // personal
            {
              path: PATH_PAGE.app.account.personal.root,
              children: [
                {
                  element: <PersonalInfoPage />,
                  path: "",
                },
                {
                  element: <ChangeEmailPage />,
                  path: PATH_PAGE.app.account.personal.changeEmail,
                },
                {
                  element: <VerifyChangedEmailPage />,
                  path: PATH_PAGE.app.account.personal.verifyChangedEmail,
                },
                {
                  element: <ChangePhonePage />,
                  path: PATH_PAGE.app.account.personal.changePhone,
                },
                {
                  element: <VerifyChangedPhonePage />,
                  path: PATH_PAGE.app.account.personal.verifyChangedPhone,
                },
              ],
            },

            // sub-accounts
            {
              element: <SubAccountsPage />,
              path: PATH_PAGE.app.account.subAccounts,
            },

            // security
            {
              path: PATH_PAGE.app.account.security.root,
              children: [
                {
                  element: <SecurityPage />,
                  path: "",
                },
                {
                  element: <ChangePasswordPage />,
                  path: PATH_PAGE.app.account.security.changePassword,
                },
                //from onboarding
                {
                  element: <PINCodePage />,
                  path: PATH_PAGE.app.account.security.pinCode,
                },
                {
                  element: <ChangePINPage />,
                  path: PATH_PAGE.app.account.security.changePIN,
                },
              ],
            },

            //! verification
            // {
            //   element: <CitizenshipVerificationPage />,
            //   path: PATH_PAGE.app.account.citizenship,
            // },
            // {
            //   element: <IdentityVerification />,
            //   path: PATH_PAGE.app.account.identityVerification,
            // },
            // {
            //   element: <RegisterGenderPage />,
            //   path: PATH_PAGE.app.account.registerGender,
            // },
            // {
            //   element: <RegisterBirthdayPage />,
            //   path: PATH_PAGE.app.account.registerBirthday,
            // },
            // {
            //   element: <RegisterCitizenshipPage />,
            //   path: PATH_PAGE.app.account.registerCitizenship,
            // },
            // {
            //   element: <ChooseDocTypePage />,
            //   path: PATH_PAGE.app.account.chooseDocType,
            // },
            // {
            //   element: <SelfieWithDocumentPage />,
            //   path: PATH_PAGE.app.account.selfieWithDocumentPage,
            // },
            // {
            //   element: <ProofOfAddressPage />,
            //   path: PATH_PAGE.app.account.proofOfAddressAddressPhotoPage,
            // },
            // {
            //   element: <UtilityBillPhotoPage />,
            //   path: PATH_PAGE.app.account.utilityBillPhotoPage,
            // },
            // {
            //   element: <PersonalAddressPage />,
            //   path: PATH_PAGE.app.account.personalAddress,
            // },
            // {
            //   element: <SignContractPage />,
            //   path: PATH_PAGE.app.account.signBusinessContract,
            // },
            //! verification

            {
              element: <QuestionsPage />,
              path: PATH_PAGE.app.account.questions,
            },

            {
              element: <SignUserContractPage />,
              path: PATH_PAGE.app.account.signContract,
            },

            {
              element: <TaxInformationPage />,
              path: PATH_PAGE.app.account.taxInformation,
            },

            // assignments
            {
              element: <AssignmentsPage />,
              path: PATH_PAGE.app.account.assignments,
            },
            {
              element: <AssignmentPage />,
              path: PATH_PAGE.app.account.assignment,
            },
            {
              element: <AssignmentSignContractPage />,
              path: PATH_PAGE.app.account.assignmentSignContract,
            },
            {
              element: <AssignmentsHistoryPage />,
              path: PATH_PAGE.app.account.assignmentsHistory,
            },
            {
              element: <AssignmentsHistoryPage />,
              path: PATH_PAGE.app.account.assignmentsIndividualHistory,
            },

            // repo
            {
              element: <RepoDeal />,
              path: PATH_PAGE.app.account.repoDeals,
            },
            {
              element: <RepoTransaction />,
              path: PATH_PAGE.app.account.repoTransaction,
            },
            {
              element: <RepoTransactionHistory />,
              path: PATH_PAGE.app.account.repoTransactionHistory,
            },

            {
              element: <TariffsPage />,
              path: PATH_PAGE.app.account.tariffs,
            },

            {
              element: <MyBonusesPage />,
              path: PATH_PAGE.app.account.myBonuses,
            },

            // deposit
            {
              element: <DepositPage />,
              path: PATH_PAGE.app.account.deposit,
            },
            {
              element: <WithdrawalPage />,
              path: PATH_PAGE.app.account.withdrawal,
            },

            {
              element: <HistoryPage />,
              path: PATH_PAGE.app.account.history.root,
            },
            {
              element: <HistorySearchPage />,
              path: PATH_PAGE.app.account.history.historySearch,
            },

            {
              element: <BrokerageReportPage />,
              path: PATH_PAGE.app.account.brokerageReport,
            },
            {
              element: <TaxReportPage />,
              path: PATH_PAGE.app.account.taxReport,
            },
          ],
        },
        // Portfolio
        {
          path: PATH_PAGE.app.portfolio.root,
          children: [
            {
              element: <PortfolioPage />,
              path: "",
              // path: PATH_PAGE.app.portfolio.editRates,
            },
          ],
        },
        // Currencies
        {
          path: PATH_PAGE.app.currencies.root,
          children: [
            {
              path: "",
              element: <CurrenciesPage />,
            },
            {
              path: PATH_PAGE.app.currencies.currencyOrder,
              element: <CurrencyOrder />,
            },
          ],
        },
      ],
    },
    { path: "404", element: <Page404 /> },
    { path: "*", element: <Navigate to={PATH_LIST.login} replace /> },
  ]);
}

// import { lazy, type ComponentType } from "react";
// import { Navigate, useRoutes } from "react-router-dom";
// import { AppGuard, OnboardGuard, sessionModel } from "entities/session";
// import { AuthLayout, MainLayout, OnBoardingLayout } from "pages/layouts";
// import { PATH_LIST, PATH_PAGE } from "shared/lib/react-router";
// import { Loadable } from "shared/ui/loadable";
// import { AppLayout } from "pages/layouts/app-layout";
// import { VerificationLayout } from "pages/app/verification-steps/verification-layout";
// import { LoginPage, WelcomeScreen } from "pages/auth";

// const Page404 = Loadable(lazy(() => import("pages/page-404")));

// const lazyPage = (
//   importer: () => Promise<Record<string, unknown>>,
//   exportName: string
// ) =>
//   Loadable(
//     lazy(async () => {
//       const module = await importer();
//       const Component = module[exportName] as ComponentType | undefined;

//       if (!Component) {
//         throw new Error(`Component "${exportName}" not found in module.`);
//       }

//       return { default: Component };
//     })
//   );

// // Core app pages
// const HomePage = lazyPage(() => import("pages/app/home"), "HomePage");
// const MarketPage = lazyPage(() => import("pages/app/market"), "MarketPage");
// const ChatPage = lazyPage(() => import("pages/app/chat"), "ChatPage");
// const SupportChatPage = lazyPage(
//   () => import("pages/app/chat-support"),
//   "SupportChatPage"
// );
// const WelcomePage = lazyPage(
//   () => import("pages/app/verification-steps/welcome"),
//   "WelcomePage"
// );
// const VerifyChangedPhonePage = lazyPage(
//   () => import("pages/app/verify-phone"),
//   "VerifyChangedPhonePage"
// );

// // Auth pages
// const ForgotPasswordPage = Loadable(
//   lazy(() =>
//     import("pages/auth").then((module) => ({
//       default: module.ForgotPasswordPage,
//     }))
//   )
// );
// const ForgotPasswordVerifyPage = Loadable(
//   lazy(() =>
//     import("pages/auth").then((module) => ({
//       default: module.ForgotPasswordVerifyPage,
//     }))
//   )
// );

// // Onboarding pages
// const ChooseAccTypePage = Loadable(
//   lazy(() =>
//     import("pages/onboarding").then((module) => ({
//       default: module.ChooseAccTypePage,
//     }))
//   )
// );
// const PINCodePage = Loadable(
//   lazy(() =>
//     import("pages/onboarding").then((module) => ({
//       default: module.PINCodePage,
//     }))
//   )
// );
// const CreateAccountPage = Loadable(
//   lazy(() =>
//     import("pages/onboarding").then((module) => ({
//       default: module.CreateAccountPage,
//     }))
//   )
// );
// const ResidencyNationalityPage = Loadable(
//   lazy(() =>
//     import("pages/onboarding").then((module) => ({
//       default: module.ResidencyNationalityPage,
//     }))
//   )
// );

// // App pages - Main features
// const DiscoverPage = lazyPage(
//   () => import("pages/app/discover"),
//   "DiscoverPage"
// );
// const ResearchPage = lazyPage(
//   () => import("pages/app/research-page"),
//   "ResearchPage"
// );
// const BlogPage = lazyPage(() => import("pages/app/blog-page"), "BlogPage");
// const StrategyPage = lazyPage(
//   () => import("pages/app/strategy"),
//   "StrategyPage"
// );
// const InvestPage = lazyPage(() => import("pages/app/invest"), "InvestPage");

// // Market pages
// const AssetSearchPage = lazyPage(
//   () => import("pages/app/asset-search"),
//   "AssetSearchPage"
// );
// const FilterPage = lazyPage(() => import("pages/app/filter"), "FilterPage");
// const CreateStockFilterPage = lazyPage(
//   () => import("pages/app/create-stock-filter"),
//   "CreateStockFilterPage"
// );
// const CreateBondFilterPage = lazyPage(
//   () => import("pages/app/create-bond-filter"),
//   "CreateBondFilterPage"
// );
// const FilterReviewPage = lazyPage(
//   () => import("pages/app/filter-review"),
//   "FilterReviewPage"
// );
// const StockDetailsPage = lazyPage(
//   () => import("pages/app/stock-details"),
//   "StockDetailsPage"
// );
// const BondDetailsPage = lazyPage(
//   () => import("pages/app/bond-details"),
//   "BondDetailsPage"
// );
// const CurrencyDetailsPage = lazyPage(
//   () => import("pages/app/currency-details"),
//   "CurrencyDetailsPage"
// );

// // Portfolio pages
// const PortfolioPage = lazyPage(
//   () => import("pages/app/portfolio"),
//   "PortfolioPage"
// );
// const BuyPage = lazyPage(() => import("pages/app/buy"), "BuyPage");
// const SellPage = lazyPage(() => import("pages/app/sell"), "SellPage");
// const HistoryPage = lazyPage(() => import("pages/app/history"), "HistoryPage");
// const HistorySearchPage = lazyPage(
//   () => import("pages/app/history-search"),
//   "HistorySearchPage"
// );

// // Account pages
// const AccountPage = lazyPage(() => import("pages/app/account"), "AccountPage");
// const PersonalInfoPage = lazyPage(
//   () => import("pages/app/personal-info"),
//   "PersonalInfoPage"
// );
// const SecurityPage = lazyPage(
//   () => import("pages/app/security"),
//   "SecurityPage"
// );
// const ChangePasswordPage = lazyPage(
//   () => import("pages/app/change-password"),
//   "ChangePasswordPage"
// );
// const ChangePINPage = lazyPage(
//   () => import("pages/app/change-PIN"),
//   "ChangePINPage"
// );
// const ChangeEmailPage = lazyPage(
//   () => import("pages/app/change-email"),
//   "ChangeEmailPage"
// );
// const ChangePhonePage = lazyPage(
//   () => import("pages/app/change-phone"),
//   "ChangePhonePage"
// );
// const VerifyChangedEmailPage = lazyPage(
//   () => import("pages/app/verify-email"),
//   "VerifyChangedEmailPage"
// );
// const TaxInformationPage = lazyPage(
//   () => import("pages/app/tax-information"),
//   "TaxInformationPage"
// );

// // Financial pages
// const DepositPage = lazyPage(() => import("pages/app/deposit"), "DepositPage");
// const WithdrawalPage = lazyPage(
//   () => import("pages/app/withdrawal"),
//   "WithdrawalPage"
// );
// const CurrenciesPage = lazyPage(
//   () => import("pages/app/currencies"),
//   "CurrenciesPage"
// );
// const CurrencyOrder = lazyPage(
//   () => import("pages/app/currency-order"),
//   "CurrencyOrder"
// );
// const RepoDeal = lazyPage(() => import("pages/app/repo-deals"), "RepoDeal");
// const RepoTransaction = lazyPage(
//   () => import("pages/app/repo-transaction"),
//   "RepoTransaction"
// );
// const RepoTransactionHistory = lazyPage(
//   () => import("pages/app/repo-transaction-history"),
//   "RepoTransactionHistory"
// );

// // Reports pages
// const BrokerageReportPage = lazyPage(
//   () => import("pages/app/brokerage-report"),
//   "BrokerageReportPage"
// );
// const TaxReportPage = lazyPage(
//   () => import("pages/app/tax-report"),
//   "TaxReportPage"
// );

// // Assignment pages
// const AssignmentPage = lazyPage(
//   () => import("pages/app/assignment-page"),
//   "AssignmentPage"
// );
// const AssignmentSignContractPage = lazyPage(
//   () => import("pages/app/assignment-sign-contract"),
//   "AssignmentSignContractPage"
// );
// const AssignmentsHistoryPage = lazyPage(
//   () => import("pages/app/assignments-history"),
//   "AssignmentsHistoryPage"
// );
// const AssignmentsPage = lazyPage(
//   () => import("pages/app/assignments"),
//   "AssignmentsPage"
// );

// // Contract pages
// const SignUserContractPage = lazyPage(
//   () => import("pages/app/sign-contract"),
//   "SignUserContractPage"
// );

// // Support pages
// const AIChatPage = lazyPage(() => import("pages/app/chat-ai"), "AIChatPage");
// const QuestionsPage = lazyPage(
//   () => import("pages/app/questions"),
//   "QuestionsPage"
// );
// const TariffsPage = lazyPage(() => import("pages/app/tariffs"), "TariffsPage");

// // Bonus pages
// const MyBonusesPage = lazyPage(
//   () => import("pages/app/my-bonuses"),
//   "MyBonusesPage"
// );

// // Verification pages
// const VerificationResidencePage = lazyPage(
//   () => import("pages/app/verification-steps/residence"),
//   "VerificationResidencePage"
// );
// const BusinessDocumentsPage = lazyPage(
//   () => import("pages/app/verification-steps/business-documents"),
//   "BusinessDocumentsPage"
// );
// const DetailsPage = lazyPage(
//   () => import("pages/app/verification-steps/details"),
//   "DetailsPage"
// );
// const DateOfBirthPage = lazyPage(
//   () => import("pages/app/verification-steps/date-of-birth"),
//   "DateOfBirthPage"
// );
// const ResidenceAddressPage = lazyPage(
//   () => import("pages/app/verification-steps/residence-address"),
//   "ResidenceAddressPage"
// );
// const PhoneNumberPage = lazyPage(
//   () => import("pages/app/verification-steps/phone-number"),
//   "PhoneNumberPage"
// );
// const EmailPage = lazyPage(
//   () => import("pages/app/verification-steps/email"),
//   "EmailPage"
// );
// const IdentityVerificationPage = lazyPage(
//   () => import("pages/app/verification-steps/identity"),
//   "IdentityVerificationPage"
// );
// const ChooseDocTypePage = lazyPage(
//   () => import("pages/app/verification-steps/choose-document-type"),
//   "ChooseDocTypePage"
// );

// const CitizenshipDocumentsPage = lazyPage(
//   () => import("pages/app/verification-steps/citizenship-documents"),
//   "CitizenshipDocumentsPage"
// );

// const SelfieWithDocumentPage = lazyPage(
//   () => import("pages/app/verification-steps/selfie-with-document"),
//   "SelfieWithDocumentPage"
// );
// const ProofOfAddressPage = lazyPage(
//   () => import("pages/app/verification-steps/proof-of-address"),
//   "ProofOfAddressPage"
// );
